import { UUID } from "@bitory-gmbh/nidavellir";
import { Summary } from "@/services/backend/htz/summary/types/summary";
import {
  PatchRequest,
  updateHtzSummaryCache,
  useHtzSummaryPatchMutation,
  useHtzSummaryShowQuery,
} from "@/services/backend/htz/summary/api/summary-api";
import { CustomerAddressCard } from "@/services/backend/addresses/components/customer-address-card";
import { ServiceAddressCard } from "@/services/backend/addresses/components/service-address-card";
import { RepresentativeCard } from "@/services/backend/representatives/components/representative-card";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { CardSkeleton } from "@/shared/components/card-skeleton";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import t from "@/lang/lang";

export function SummaryAddressesAndContacts({
  id,
  disabled,
}: {
  id: UUID;
  disabled?: boolean;
}) {
  const {
    data,
    error: dataError,
    isLoading: dataLoading,
  } = useHtzSummaryShowQuery({ id });

  return (
    <div>
      {data && <AddressesAndContacts data={data} disabled={disabled} />}
      {dataError && <RTKQueryErrorAlert error={dataError} />}
      {dataLoading && <CardSkeleton />}
    </div>
  );
}

function AddressesAndContacts({
  data,
  disabled,
}: {
  data: Summary;
  disabled?: boolean;
}) {
  const request: PatchRequest = { id: data.id };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzSummaryCache,
    useHtzSummaryPatchMutation(),
  );

  return (
    <div className="grid gap-2 xl:grid-cols-2">
      <CustomerAddressCard
        customerAddress={data.postalAddress}
        onCustomerAddressChange={(postalAddress) =>
          set({ ...request, postalAddress }, { ...data, postalAddress })
        }
        description={t(
          "Adresse, an welche der Inspektionsbericht geschickt werden soll.",
        )}
        disabled={disabled}
      />
      <ServiceAddressCard
        address={data.serviceAddress}
        onAddressChange={(serviceAddress) =>
          set({ ...request, serviceAddress }, { ...data, serviceAddress })
        }
        description={t("Adresse des Objekts, an dem inspiziert wurde.")}
        disabled={disabled}
      />
      <RepresentativeCard
        representative={data.representative}
        onRepresentativeChange={(representative) =>
          set({ ...request, representative }, { ...data, representative })
        }
        disabled={disabled}
      />
    </div>
  );
}
